/**
 * Phase 187 — Bloc "Fournisseurs & Prestataires associés" sur la fiche matériel.
 * Liste les fournisseurs uniques intervenus via un entretien, avec la raison
 * principale, le nombre d'interventions et le coût total.
 */
import { useEffect, useState } from "react";
import { api, formatDate, formatCurrency } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export const LinkedSuppliersBlock = ({ equipmentId, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!equipmentId) return;
    let cancelled = false;
    setLoading(true);
    api.getEquipmentLinkedSuppliers(equipmentId)
      .then((res) => { if (!cancelled) setRows(res.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [equipmentId, refreshKey]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="h-4 w-4 text-slate-600" />
        <h3 className="font-heading font-bold text-base">Fournisseurs & Prestataires associés</h3>
        {rows.length > 0 && <Badge variant="outline">{rows.length}</Badge>}
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground italic text-center py-4">Chargement…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground italic text-center py-4 bg-muted/30 rounded" data-testid="linked-suppliers-empty">
          Aucun fournisseur lié via un entretien. Ajoutez un entretien en renseignant le champ « Fournisseur » pour créer un lien.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="linked-suppliers-list">
          {rows.map((r) => (
            <Card key={r.supplier_id} className="border-slate-200" data-testid={`linked-supplier-${r.supplier_id}`}>
              <CardContent className="pt-3 pb-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{r.company_name}</p>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {r.main_reason && (
                        <Badge
                          variant="outline"
                          className="text-xs"
                          style={r.main_reason.color ? {
                            borderColor: r.main_reason.color,
                            color: r.main_reason.color,
                            backgroundColor: `${r.main_reason.color}12`,
                          } : {}}
                        >
                          {r.main_reason.name}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        {r.intervention_count} intervention{r.intervention_count > 1 ? "s" : ""}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => navigate(`/fournisseurs?open=${r.supplier_id}`)}
                    title="Ouvrir la fiche fournisseur"
                    data-testid={`linked-supplier-open-${r.supplier_id}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                  {r.last_intervention_date && (
                    <span>Dernière : {formatDate(r.last_intervention_date)}</span>
                  )}
                  {r.total_cost > 0 && <span>Total : {formatCurrency(r.total_cost)}</span>}
                  {r.phone && <span className="font-mono">{r.phone}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LinkedSuppliersBlock;
